import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import {
  addNewParent,
  setNewParentData,
  updateParent,
} from "../../AxiosRtk/AddNewParent";
import { AlertModal } from "../../../../setup/alert/swalAlert";
import { alertIconType } from "../../../../setup/alert/iconTypes";
import { useDispatch, useSelector } from "react-redux";
import useCrypto from "../../../customHooks/crypto";
import { getPreSignedUrl, uploadFileToS3 } from "../../AxiosRtk/Miscellaneous";
import { isSpecialCharContains } from "../../../../utils/specialCharCheck";
import { useLocation } from "react-router-dom";
import DefaultUser from "../../../../assets/images/default-user.svg";
import SuccessMessage from "../modals/SuccessMessage";

const AddNewParent = ({ getLangTranslation }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { encodeNumberToBase64 } = useCrypto();
  const history = useHistory();
  const { user: userData } = useSelector(({ auth }) => auth);
  const { newParentData } = useSelector(({ addNewParent }) => addNewParent);

  // Initial state for parent data
  const [parentData, setParentData] = useState({
    name: { value: location?.state?.savedData?.FullName || "", error: "" },
    email: { value: location?.state?.savedData?.Email || "", error: "" },
    image: {
      file: null,
      childPicUrl: "",
      error: "",
    },
  });

  const [presigned, setPresignedData] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [resMsg, setResMsg] = useState('');
  
  // State to track if form data is changed
  const [isFormChanged, setIsFormChanged] = useState(false);

  // Handle image file upload and get presigned URL
  const handleImageUpload = async (event) => {
    const payload = {
      type: "put",
      fileName: `${encodeNumberToBase64(userData?.ID)}/ProfilesPics/${event.target.files[0].name}`,
    };

    try {
      getPreSignedUrl(payload)
        .then((res) => {
          setPresignedData(res?.data?.result);
        })
        .catch((err) => {
          console.log("Error while getting presigned URL:", err?.response?.data?.message);
        });
    } catch (error) {
      console.log(error);
    }

    // Update parent data with the uploaded image details
    setParentData({
      ...parentData,
      image: {
        file: event.target.files[0],
        childPicUrl: URL.createObjectURL(event.target.files[0]),
        error: "",
      },
    });

    // Mark form as changed
    setIsFormChanged(true);
  };

  // Handle input field changes for name and email
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setParentData({
      ...parentData,
      [name]: {
        value,
        error: "",
      },
    });

    // Mark form as changed
    setIsFormChanged(true);
  };

  // API call to add new parent
  function addNewParentApi(payload) {
    addNewParent(payload)
      .then((res) => {
        setResMsg(res?.data?.message);
        setShowModal(true);
      })
      .catch((err) => {
        AlertModal(
          err?.response?.data?.message,
          alertIconType?.errorIcon,
          true
        );
      });
  }

  // API call to update parent data
  function updateParentApi(payload) {
    updateParent(payload)
      .then((res) => {
        setResMsg(res?.data?.message);
        setShowModal(true);
      })
      .catch((err) => {
        AlertModal(
          err?.response?.data?.message,
          alertIconType?.errorIcon,
          true
        );
      });
  }

  // Validate form before submission
  function validateForm() {
    let flag = true;
    const parentDataTemp = { ...parentData };

    // Name validation
    if (!parentData?.name?.value) {
      flag = false;
      parentDataTemp["name"]["value"] = "";
      parentDataTemp["name"]["error"] = "name_IS_REQUIRED";
    } else if (isSpecialCharContains(parentData?.name?.value)) {
      flag = false;
      parentDataTemp["name"]["value"] = parentData?.name?.value;
      parentDataTemp["name"]["error"] = "SPECIAL_CHAR_NOT_ALLOWED_ERR";
    }

    setParentData(parentDataTemp);
    return flag;
  }

  // Load initial data when editing an existing parent
  useEffect(() => {
    if (location?.pathname === '/edit-new-parent') {
      setParentData({
        name: { value: newParentData?.FullName, error: '' },
        email: { value: newParentData?.Email, error: '' },
        image: {
          file: null,
          childPicUrl: `${process.env.REACT_APP_IMAGE_BUCKET_PHASE2_DEV}/${encodeNumberToBase64(userData?.ID)}/ProfilesPics/${newParentData?.AdditionalParentImage}`,
          error: ''
        }
      });
    }
  }, [location, newParentData]);

  // Track changes in form fields (name, email, image)
  useEffect(() => {
    // Only set form changed to true if data is modified
    if (
      parentData.name.value !== location?.state?.savedData?.FullName ||
      parentData.email.value !== location?.state?.savedData?.Email ||
      parentData.image.file !== null
    ) {
      setIsFormChanged(true);  // Mark as changed
    } else {
      setIsFormChanged(false);  // Mark as not changed
    }
  }, [parentData, location]);

  // Handle form save
  function handleSaveData() {
    if (!validateForm()) {
      return;  // Stop if form validation fails
    }

    // Logic for editing an existing parent
    if (location?.pathname === '/edit-new-parent') {
      try {
        if (!parentData?.image?.file) {
          const payload = {
            name: parentData?.name?.value,
            email: parentData?.email?.value,
            id: newParentData?.ID,
          };
          updateParentApi(payload);
        } else {
          // Upload image to S3 if new image is uploaded
          uploadFileToS3(
            `${presigned?.signedUrlForPutObject}`,
            parentData?.image?.file,
            {
              "Content-Type": parentData?.image?.file.type,
              version_code: 20000,
            }
          )
            .then((res) => {
              const payload = {
                name: parentData?.name?.value,
                email: parentData?.email?.value,
                image: `${process.env.REACT_APP_IMAGE_BUCKET_PHASE2_DEV}/${encodeNumberToBase64(userData?.ID)}/ProfilesPics/${parentData?.image?.file?.name}`,
                id: newParentData?.ID
              };
              updateParentApi(payload);
            })
            .catch((error) => {
              console.log("Error while uploading image:", error?.response?.data?.message);
            });
        }
        dispatch(setNewParentData(null));  // Clear new parent data from redux
      } catch (e) {
        console.log(e);
      }
    } else {
      // Logic for adding a new parent
      try {
        if (!parentData?.image?.file) {
          const payload = {
            name: parentData?.name?.value,
            email: parentData?.email?.value,
          };
          addNewParentApi(payload);
        } else {
          // Upload image to S3 if new image is uploaded
          uploadFileToS3(
            `${presigned?.signedUrlForPutObject}`,
            parentData?.image?.file,
            {
              "Content-Type": parentData?.image?.file.type,
              version_code: 20000,
            }
          )
            .then((res) => {
              const payload = {
                name: parentData?.name?.value,
                image: `${process.env.REACT_APP_IMAGE_BUCKET_PHASE2_DEV}/${encodeNumberToBase64(userData?.ID)}/ProfilesPics/${parentData?.image?.file?.name}`,
                email: parentData?.email?.value
              };
              addNewParentApi(payload);
            })
            .catch((error) => {
              console.log("Error while uploading image:", error?.response?.data?.message);
            });
        }
      } catch (e) {
        console.log(e);
      }
    }
  }

  // Function to close the modal and redirect to the parent list page
  function closeModal() {
    setShowModal(false);
    history.push("/parent-list");
  }

  return (
    <>
      <div className="card">
        <div className="card-body px-9 py-9">
          <div className="content-550 mx-auto">
            {
              location?.pathname === '/edit-new-parent' ? (
                <h6
                  className='text-blue-900 mb-0 c-pointer'
                  onClick={() => history.push("/parent-list")}
                >
                  <i className='icon-arrow-left' /> <span className="fs-6">{getLangTranslation("EDIT_PARENT", "Edit parent")}</span>
                </h6>
              ) : (
                <>
                  <h2 className="text-center mb-3">{getLangTranslation("ADD_NEW_PARENT", "Add new parent")}</h2>
                  <p className="text-center mb-6 light-text">
                    {getLangTranslation("ADD_NEW_PARENT_THIS_FEATURE_ALLOWS", "This feature allows the registered parent to add an extra parent or a family member to help him in monitoring and supervising his parents. The new member will have the same rights as the original parent.")}
                  </p>
                </>
              )
            }

            <div className="d-flex flex-column justify-content-center align-items-center">
              <div className="image-placeholder add-image ratio ratio-1x1 mb-3">
                {
                  parentData?.image?.childPicUrl ? (
                    <img
                      src={parentData?.image?.childPicUrl}
                      alt='Child Pic'
                      className="rounded-circle"
                      onError={(event) => {
                        event.target.onerror = null;
                        event.target.src = DefaultUser;
                      }}
                    />
                  ) : (
                    <i
                      className="icon-placeholder text-blue-600 fs-1 top-50 start-50 w-auto h-auto translate-middle"
                    />
                  )
                }
              </div>
              <button className="btn btn-outline-primary custom-file-wrap d-inline-flex align-items-center gap-1 mb-6">
                <i className="icon-plus fs-6" />
                {getLangTranslation("ADD_PARENT_AVATAR", "Add parent avatar")}
                <input
                  name="childPic"
                  onChange={(e) => handleImageUpload(e)}
                  type="file"
                  accept=".png, .jpg, .jpeg, .svg"
                />
              </button>
            </div>
            <div className="mb-6">
              <label htmlFor="name" className="form-label">
                {getLangTranslation("ACCOUNT_SETTINGS_PARENT_FULL_NAME", "Full Name")}
              </label>
              <input
                type="text"
                className="form-control"
                id="name"
                placeholder={getLangTranslation("ADD_PARENT_HINT_NAME", "Name")}
                name="name"
                defaultValue={parentData?.name?.value}
                onChange={handleInputChange}
              />
              {parentData?.name?.error && (
                <label className="field-error">
                  {getLangTranslation(parentData?.name?.error)}
                </label>
              )}
            </div>
            <div className="mb-6">
              <label htmlFor="email" className="form-label">
                {getLangTranslation("EMAIL", "Email")}
              </label>
              <input
                type="text"
                className="form-control"
                id="email"
                placeholder={getLangTranslation("ADD_PARENT_EMAIL_ID", "Email ID")}
                name="email"
                disabled={location?.pathname === '/edit-new-parent' ? true : false}
                defaultValue={parentData?.email?.value}
                onChange={handleInputChange}
              />
            </div>
            <div className="grid gap-3">
              <button
                className="btn btn-primary g-col-6"
                type="button"
                onClick={handleSaveData}
                disabled={!isFormChanged}  // Disable Save button if no changes
              >
                <span className="btn-upper-case">{getLangTranslation("SAVE", "Save")}</span>
              </button>
              <button
                className="btn btn-outline-primary g-col-6"
                type="button"
                onClick={closeModal}
              >
                <span className="btn-upper-case">
                  {getLangTranslation("CANCEL", "Cancel")}
                </span>
              </button>
            </div>

          </div>
        </div>
      </div>
      <SuccessMessage
        showModal={showModal}
        closeModal={closeModal}
        getLangTranslation={getLangTranslation}
        message={resMsg}
        value={resMsg}
      />
    </>
  );
};

export default AddNewParent;
